import { jsx as _jsx } from "react/jsx-runtime";
import { EvaluationProvider, Evaluations, Spinner } from "@madmedical/ui";
import { getAlertLevelChoices, getMetricTypeChoices, } from "@madmedical/medical";
import { useGetTreatingDoctorsQuery, usePatientId } from "@madmedical/user";
import { invariant, useDebounce } from "@madmedical/utils";
import { useCallback, useMemo, useState } from "react";
import { useGetOpinionsQuery } from "../api";
import adaptOpinion from "../adaptOpinion";
// Do not get confused, opinions === (doctor's) evaluations, at least for now TODO: Clear it up
const Opinions = () => {
    const { patientId } = usePatientId();
    const { data: doctors } = useGetTreatingDoctorsQuery(patientId);
    const [page, setPage] = useState(1);
    const [filter, setFilter] = useState({});
    const [search, setSearch] = useState();
    const debouncedSearch = useDebounce(search, 300, () => {
        setPage(1);
    });
    const { data, isLoading } = useGetOpinionsQuery(Object.assign(Object.assign({ patientId }, filter), { search: debouncedSearch, page }), {
        refetchOnMountOrArgChange: true,
        refetchOnReconnect: true,
        refetchOnFocus: true,
        pollingInterval: 30000,
    });
    const filterChoices = useMemo(() => {
        var _a;
        return ({
            metricTypes: getMetricTypeChoices(filter.metricTypes),
            alertLevels: getAlertLevelChoices(filter.alertLevel),
            doctors: (_a = doctors === null || doctors === void 0 ? void 0 : doctors.map(({ userId, fullName }) => {
                var _a;
                return ({
                    key: userId,
                    text: fullName,
                    isSelected: !!((_a = filter.doctorIds) === null || _a === void 0 ? void 0 : _a.includes(userId)),
                });
            })) !== null && _a !== void 0 ? _a : [],
        });
    }, [filter, doctors]);
    const handleFilterChange = useCallback((key, selected) => {
        setPage(1);
        // TODO: Why does type inference fail me?
        const diff = key === "metricTypes"
            ? { metricTypes: selected }
            : key === "alertLevels"
                ? { alertLevel: selected[0] }
                : key === "doctors"
                    ? { doctorIds: selected }
                    : {};
        setFilter((prevFilter) => (Object.assign(Object.assign({}, prevFilter), diff)));
    }, []);
    const handleSearchChange = (search) => {
        setSearch(search);
    };
    const handleSortChange = useCallback((sort, order) => {
        setPage(1);
        if (!sort) {
            setFilter((prevFilter) => (Object.assign(Object.assign({}, prevFilter), { sort: undefined, order: undefined })));
            return;
        }
        setFilter((prevFilter) => (Object.assign(Object.assign({}, prevFilter), { sort: sort, // TODO: Better types
            order })));
    }, []);
    const handleEndReached = useCallback(() => {
        if (!data) {
            return;
        }
        const { currentPage, pageCount } = data.pagination;
        if (currentPage >= pageCount) {
            return;
        }
        setPage(currentPage + 1);
    }, [data]);
    if (isLoading) {
        return _jsx(Spinner, {});
    }
    invariant(data, "No opinions in sight");
    const { items: opinions, pagination } = data;
    return (_jsx(EvaluationProvider, Object.assign({ value: {
            evaluations: opinions.map(adaptOpinion),
            onSortChange: handleSortChange,
            onSearchChange: handleSearchChange,
            filterChoices,
            onFilterChange: handleFilterChange,
            pagination,
        } }, { children: _jsx(Evaluations, { onEndReached: handleEndReached }) })));
};
export default Opinions;
